import AjaxUtils from "../../util/ajax_utils";

export const ApckWorkflowNotesTables = {
    props: {
        notes: {
            type: Array,
            default: () => []
        }
    },
    data: function () {
        return {
            expanded: false,
        }
    },
    template: `
        <div class="notes-history-section">
            <div class="expand-toggle" @click="expanded = !expanded">
                <span>Notes History</span>
                <i v-if="expanded" class="fa-regular fa-chevron-up"></i>
                <i v-else class="fa-regular fa-chevron-down"></i>
            </div>
            <table v-show="expanded" class="notes-history">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Username</th>
                        <th>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="note in notes">
                        <td>{{ note.created | formatDateTime }}</td>
                        <td>{{ note.user_summary ? note.user_summary.name : 'N/A' }}</td>
                        <td>{{ note.notes }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `
}

export const ApckWorkflowForm = {
    components: {
        'apck-workflow-notes-table': ApckWorkflowNotesTables,
    },
    props: {
        url: String,
        errors: {
            type: Object,
            default: () => ({})
        },
    },
    data: function () {
        return {
            assignee: null,
            assignee_options: [],
            status: null,
            status_options: [
                {'label': 'Unfixed', 'value': 'unfixed'},
                {'label': 'Fixed', 'value': 'fixed'},
                {'label': 'False Positive', 'value': 'false_positive'},
                {'label': 'Acceptable Risk', 'value': 'acceptable_risk'},
                {'label': 'Backported', 'value': 'backported'},
                {'label': 'Investigating', 'value': 'investigating'}
            ],
            suppression: null,
            suppression_options: [
                {'label': '14 Days', 'value': 14},
                {'label': '30 Days', 'value': 30},
                {'label': '60 Days', 'value': 60},
                {'label': '90 Days', 'value': 90},
                {'label': '6 Months', 'value': 180},
                {'label': '1 Year', 'value': 366},
                {'label': 'Forever', 'value': 99999},
            ],
            priority: null,
            priority_options: [
                {'label': '--Not Assigned--', 'value': null},
                {'label': 'High', 'value': '4.0-high'},
                {'label': 'Medium', 'value': '3.0-medium'},
                {'label': 'Low', 'value': '2.0-low'},
            ],
            category: null,
            category_options: [
                {'label': 'Application', 'value': 'web_app'},
                {'label': 'Infrastructure', 'value': 'infra'},
            ],
            notes: '',
            notes_history: [],
            loaded: true,
        };
    },
    computed: {
        _assignee_options () {
            return [
                {'label': '--- unassigned ---', 'value': null},
                ...this.assignee_options,
            ];
        },
        suppression_eligible() {
            return ['false_positive', 'acceptable_risk'].indexOf(this.status) !== -1;
        },
        suppression_description() {
            return this.suppression_eligible ? '' : 'Suppression periods can only be applied to vulnerabilities marked as False Positive or Acceptable Risk.';
        }
    },
    methods: {
        save: function () {
            var cmp = this;
            cmp.loaded = false;

            const data = {
                'assignee': cmp.assignee,
                'status': cmp.status,
                'suppression': cmp.suppression,
                'priority': cmp.priority,
                'category': cmp.category,
                'notes': cmp.notes,
            };
            AjaxUtils.ajax_post({
                url: this.url,
                data: JSON.stringify(data),
                success: function (json, status, xhr) {
                    cmp.loaded = true;
                    cmp.$emit('update:errors', {});
                    cmp.$emit('workflow-updated');
                },
                fail: function (json, status, xhr) {
                    cmp.$emit('update:errors', json?.field_errors ? json.field_errors : {});
                    cmp.loaded = true;
                }
            });
        },
    },
    template: `
        <div class="row workflow-form-tab">
            <div class="col col-xs-6">
                <apck-field :errors="errors?.assignee">
                    <apck-select
                        v-model="assignee"
                        label="Assignee"
                        :options="_assignee_options"
                    />
                </apck-field>

                <apck-field :errors="errors?.status">
                    <apck-select
                        v-model="status"
                        label="Status"
                        :options="status_options"
                    />
                </apck-field>

                <apck-field :errors="errors?.suppression">
                    <apck-select
                        v-model="suppression"
                        label="Suppress vulnerability for"
                        :options="suppression_options"
                        :disabled="!suppression_eligible"
                    />
                    <p v-if="suppression_description" class="option-note">{{ suppression_description }}</p>
                </apck-field>

                <apck-field :errors="errors?.priority">
                    <apck-select
                        v-model="priority"
                        label="Priority"
                        :options="priority_options"
                    />
                </apck-field>

                <apck-field :errors="errors?.category">
                    <apck-select
                        v-model="category"
                        label="Category"
                        :options="category_options"
                    />
                </apck-field>

                <apck-workflow-notes-table
                    :notes="notes_history"
                />

                <apck-field :errors="errors?.notes">
                    <apck-text
                        v-model="notes"
                        label="Notes"
                        :rows="10"
                    />
                </apck-field>

                <apck-button title="Save" value="Save" icon="fa-save" class="btn-positive" :handler="save"/>
                <apck-mask :trigger="!loaded"/>
            </div>
            <div class="col col-xs-6 workflow-description">
                <div>
                    <h4>De-Duplication and Vulnerability Suppression</h4>
                    <p>
                    When a vulnerability is received by AppCheck we attempt to de-duplicate it.
                    </p>
                    <p>
                    Using a number of values (category, target, title, host, port, parameter, payload) we generate a unique signature for that vulnerability using a hash.
                    </p>
                    <p>
                    If any of these values change then the vulnerability will be reported as new and will receive a new tracking ID because the hash will have changed.
                    </p>
                    <p>
                    If the vulnerability is an <strong>INFO</strong> level vulnerability, then it is never de-duplicated as INFO findings contain useful information related to that particular run of the scan that can be helpful for debugging or looking back at point in time information.
                    </p>
                    <p>
                    When a vulnerability is marked as fixed, regardless of suppression period if we detect it again it will be marked as unfixed,
                    the reason for this, is that if you report an issue as fixed and it's detected again in a scan, it's considered that you would want
                    to know this.
                    </p>
                    <p>
                    If you want to apply a suppression period to the vulnerability and understand the risks, it needs to be marked as a <strong>"False Positive"</strong> or <strong>"Acceptable Risk"</strong>.
                    </p>
                    <p>
                    If the vulnerability is then re-detected after the suppression period, <i>"Set back to unfixed due to detection after suppression expired"</i> will be added to the notes.
                    </p>
                </div>
            </div>
        </div>
    `
}
